import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import Button from "../../components/common/Button";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import stockCategoryService from "../../services/stockCategoryService";
import lotService from "../../services/lotService";
import reportService from "../../services/reportService";
import { formatDate } from "../../utils/dateUtils";

function formatNumber(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(number);
}

function formatWeight(value) {
  return `${formatNumber(value)} kg`;
}

function formatCurrency(value) {
  const number = Number(value || 0);
  return `₹ ${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(number)}`;
}

function formatRate(value) {
  const number = Number(value || 0);
  return `₹ ${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 4,
    minimumFractionDigits: 2,
  }).format(number)}`;
}

function getDetailAddition(detail) {
  return Number(
    detail?.addition ??
      detail?.addedStock ??
      detail?.additionStock ??
      detail?.additionWeight ??
      detail?.addedWeight ??
      0,
  );
}

function getDetailDeduction(detail) {
  return Number(
    detail?.deduction ??
      detail?.deductedStock ??
      detail?.deductionStock ??
      detail?.deductionWeight ??
      detail?.deductedWeight ??
      0,
  );
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function generateExcelHtmlFromData(rows) {
  let htmlRows = [];

  // Main Title
  htmlRows.push(`
    <tr>
      <td colspan="8" style="font-size: 14px; font-weight: bold; text-align: center; padding: 10px; background-color: #f1f5f9; border: 1px solid #cbd5e1;">
        STOCK REPORT
      </td>
    </tr>
    <tr><td colspan="8" style="height: 5px;"></td></tr>
  `);

  if (!rows || rows.length === 0) {
    htmlRows.push(`
      <tr>
        <td colspan="8" style="text-align: center; padding: 15px; color: #64748b; font-size: 12px;">No records found</td>
      </tr>
    `);
  } else {
    rows.forEach((item, index) => {
      // Category Grouping Headers
      htmlRows.push(`
        <tr style="color: #000000; font-weight: bold; text-align: center; font-size: 12px;">
          <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 6px;">Sr. No.</td>
          <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 6px;">Stock Category</td>
          <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 6px;">Addition</td>
          <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 6px;">Deduction</td>
          <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 6px;">Remaining Stock</td>
          <td style="background-color: #ffffff; padding: 6px;" colspan="2"></td>
        </tr>
      `);

      // Category Main Data
      htmlRows.push(`
        <tr style="font-weight: bold; font-size: 12px;">
          <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: center;">${index + 1}</td>
          <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: center;">${escapeHtml(item.stockCategoryName)}</td>
          <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: right; color: #008000;">${formatWeight(item.totalAddition)}</td>
          <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: right; color: #ff0000;">${formatWeight(item.totalDeduction)}</td>
          <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: right; color: #0000ff;">${formatWeight(item.remainingStock)}</td>
          <td style="background-color: #ffffff; padding: 6px;" colspan="2"></td>
        </tr>
      `);

      const stockData = item.stockData || [];
      if (stockData.length > 0) {
        // Nested Table Header (Indented)
        htmlRows.push(`
          <tr style="color: #000000; font-weight: bold; font-size: 12px; text-align: center;">
            <td style="background-color: #ffffff; border-right: 1px solid #94a3b8;"></td>
            <td style="background-color: #C6E0B4; border: 1px solid #94a3b8; padding: 6px;">Date</td>
            <td style="background-color: #C6E0B4; border: 1px solid #94a3b8; padding: 6px;">Lot</td>
            <td style="background-color: #C6E0B4; border: 1px solid #94a3b8; padding: 6px;">Addition</td>
            <td style="background-color: #C6E0B4; border: 1px solid #94a3b8; padding: 6px;">Deduction</td>
            <td style="background-color: #C6E0B4; border: 1px solid #94a3b8; padding: 6px;">Processed</td>
            <td style="background-color: #C6E0B4; border: 1px solid #94a3b8; padding: 6px;">Cumulative</td>
          </tr>
        `);

        // Nested Data Rows
        stockData.forEach((detail) => {
          const add = getDetailAddition(detail);
          const ded = getDetailDeduction(detail);

          htmlRows.push(`
            <tr style="font-size: 12px;">
              <td style="background-color: #ffffff; border-right: 1px solid #94a3b8;"></td>
              <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: center;">${formatDate(detail.transactionDate)}</td>
              <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: center;">${escapeHtml(detail.lotNo || detail.sourceLotNo || "-")}</td>
              <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: right; color: #008000;">${add > 0 ? '+' + formatWeight(add) : '-'}</td>
              <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: right; color: #ff0000;">${ded > 0 ? '-' + formatWeight(ded) : '-'}</td>
              <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: right; color: #6366f1;">${detail.processedWeight > 0 ? formatWeight(detail.processedWeight) : '-'}</td>
              <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: right; font-weight: bold; color: #0000ff;">${formatWeight(detail.cumulativeStock)}</td>
            </tr>
          `);
        });
      } else {
         htmlRows.push(`
          <tr>
            <td style="border-right: 1px solid #94a3b8; background-color: #ffffff;"></td>
            <td colspan="8" style="border: 1px solid #94a3b8; padding: 6px; text-align: center; color: #64748b; font-size: 10px;">No transaction records found.</td>
          </tr>
        `);
      }

      // Add a spacer row between categories
      htmlRows.push(`<tr><td colspan="9" style="height: 15px;"></td></tr>`);
    });
  }

  const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8" />
<style>
  table { border-collapse: collapse; font-family: Calibri, Arial, sans-serif; font-size: 10px; }
</style>
</head>
<body>
<table>
${htmlRows.join("\n")}
</table>
</body>
</html>`;

  return html;
}

function generatePDFFromData(rows) {
  const doc = new jsPDF({
    orientation: "portrait", // Can go back to portrait now with fewer columns
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Title
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("STOCK REPORT", pageWidth / 2, 15, { align: "center" });

  let finalY = 25;

  if (!rows || rows.length === 0) {
    doc.setFontSize(10);
    doc.text("No records found", pageWidth / 2, finalY, { align: "center" });
    return doc;
  }

  rows.forEach((item, index) => {
    // Check if we need a new page before starting a new category
    if (finalY > 240) {
      doc.addPage();
      finalY = 20;
    }

    // Category Summary Table
    autoTable(doc, {
      startY: finalY,
      head: [["Sr. No.", "Stock Category", "Addition", "Deduction", "Remaining Stock"]],
      body: [[
        index + 1,
        item.stockCategoryName,
        formatWeight(item.totalAddition),
        formatWeight(item.totalDeduction),
        formatWeight(item.processedWeight),
        formatWeight(item.remainingStock),
      ]],
      theme: "grid",
      headStyles: { 
        fillColor: [189, 215, 238], 
        textColor: [0, 0, 0], 
        halign: "center",
        fontSize: 9,
        lineWidth: 0.1
      },
      bodyStyles: { 
        fontSize: 9, 
        fontStyle: "bold",
        lineWidth: 0.1
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 15 },
        1: { halign: "center" },
        2: { halign: "right", textColor: [0, 128, 0] },
        3: { halign: "right", textColor: [255, 0, 0] },
        4: { halign: "right", textColor: [99, 102, 241] },
        5: { halign: "right", textColor: [0, 0, 255] },
      },
      margin: { left: 10, right: 10 },
    });

    finalY = doc.lastAutoTable.finalY;

    // Transaction Details Table
    const stockData = item.stockData || [];
    const detailsBody = stockData.map(detail => {
      const add = getDetailAddition(detail);
      const ded = getDetailDeduction(detail);
      return [
        formatDate(detail.transactionDate),
        detail.lotNo || detail.sourceLotNo || "-",
        add > 0 ? `+${formatWeight(add)}` : "-",
        ded > 0 ? `-${formatWeight(ded)}` : "-",
        detail.processedWeight > 0 ? formatWeight(detail.processedWeight) : "-",
        formatWeight(detail.cumulativeStock)
      ];
    });

    if (detailsBody.length > 0) {
      autoTable(doc, {
        startY: finalY,
        head: [["Date", "Lot", "Addition", "Deduction", "Processed", "Cumulative"]],
        body: detailsBody,
        theme: "grid",
        headStyles: { 
          fillColor: [198, 224, 180], 
          textColor: [0, 0, 0], 
          halign: "center",
          fontSize: 8,
          lineWidth: 0.1
        },
        bodyStyles: { 
          fontSize: 8,
          lineWidth: 0.1
        },
        columnStyles: {
          0: { halign: "center" },
          1: { halign: "center", fontStyle: "bold" },
          2: { halign: "center" },
          3: { halign: "center" },
          4: { halign: "right", textColor: [0, 128, 0] },
          5: { halign: "right", textColor: [255, 0, 0] },
          6: { halign: "right", textColor: [99, 102, 241] },
          7: { halign: "right", textColor: [0, 0, 255], fontStyle: "bold" },
        },
        margin: { left: 25, right: 10 }, // Indent like Excel
      });
      finalY = doc.lastAutoTable.finalY + 10;
    } else {
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text("No transaction records found.", 30, finalY + 5);
      finalY += 15;
    }
    doc.setTextColor(0); // Reset text color
  });

  return doc;
}

function StockReportPage() {
  const navigate = useNavigate();
  const { stockCategoryId } = useEncryptedParams();
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    stockCategoryId || "",
  );
  const [selectedLotId, setSelectedLotId] = useState("");
  const [activeCategories, setActiveCategories] = useState([]);
  const [activeLots, setActiveLots] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState("");
  const [error, setError] = useState("");
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    setSelectedCategoryId(stockCategoryId || "");
  }, [stockCategoryId]);

  const loadOptions = useCallback(async () => {
    setIsOptionsLoading(true);
    try {
      const [categoriesData, lotsData] = await Promise.all([
        stockCategoryService.getActiveStockCategories(),
        lotService.getActiveLots({ showLocked: true }),
      ]);
      setActiveCategories(categoriesData.items || []);
      setActiveLots(lotsData.items || []);
    } catch (err) {
      setError(err?.message || "Failed to load filter options.");
    } finally {
      setIsOptionsLoading(false);
    }
  }, []);

  const loadReport = useCallback(async (currentCategoryId, currentLotId) => {
    setIsLoading(true);
    setError("");
    try {
      const report = await reportService.getStockReport(
        currentCategoryId,
        currentLotId,
      );
      setRows(report.items || []);
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to load stock report.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    loadReport(selectedCategoryId, selectedLotId);
  }, [selectedCategoryId, selectedLotId, loadReport]);

  const categoryOptions = useMemo(() => {
    const options = activeCategories.map((cat) => ({
      value: String(cat.id || cat.stockCategoryId),
      label: cat.name || cat.stockCategoryName,
    }));

    return [{ value: "", label: "All Categories" }, ...options];
  }, [activeCategories]);

  const lotOptions = useMemo(() => {
    const options = activeLots.map((lot) => ({
      value: String(lot.lotId),
      label: lot.lotNo || "-",
    }));

    return [{ value: "", label: "All Lots" }, ...options];
  }, [activeLots]);

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    setSelectedCategoryId(value);
    if (!selectedLotId) {
      navigate(
        value ? `/reports/stock-report/${encrypt(value)}` : "/reports/stock-report",
      );
    }
  };

  const handleLotChange = (event) => {
    setSelectedLotId(event.target.value);
  };

  const handleRefresh = () => {
    loadOptions();
    loadReport(selectedCategoryId, selectedLotId);
  };

  const handleDownload = async (format) => {
    setDownloadingFormat(format);
    setError("");
    try {
      const scope = [];
      if (selectedCategoryId) scope.push(`cat-${selectedCategoryId}`);
      if (selectedLotId) scope.push(`lot-${selectedLotId}`);
      const scopeStr = scope.length > 0 ? scope.join("-") : "all";
      
      if (format === "csv") {
        const html = generateExcelHtmlFromData(rows);
        const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
        downloadBlob(blob, `stock-report-${scopeStr}.xls`);
      } else if (format === "pdf") {
        const doc = generatePDFFromData(rows);
        doc.save(`stock-report-${scopeStr}.pdf`);
      } else {
        const blob = await reportService.downloadStockReport(
          format,
          selectedCategoryId,
          selectedLotId,
        );
        downloadBlob(blob, `stock-report-${scopeStr}.${format}`);
      }
    } catch (err) {
      setError(
        err?.message || `Failed to download ${format.toUpperCase()} report.`,
      );
    } finally {
      setDownloadingFormat("");
    }
  };

  const toggleRow = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 dark:border-slate-800 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Reports
          </p>
          <h1 className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
            Stock Report
          </h1>
        </div>

        <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto lg:items-end">
          <div className="w-full sm:min-w-[200px]">
            <SearchableDropdown
              label="Stock Category"
              name="stockCategoryId"
              value={selectedCategoryId}
              onChange={handleCategoryChange}
              options={categoryOptions}
              placeholder={isOptionsLoading ? "Loading..." : "All Categories"}
              disabled={isOptionsLoading}
            />
          </div>
          <div className="w-full sm:min-w-[200px]">
            <SearchableDropdown
              label="Lot"
              name="lotId"
              value={selectedLotId}
              onChange={handleLotChange}
              options={lotOptions}
              placeholder={isOptionsLoading ? "Loading..." : "All Lots"}
              disabled={isOptionsLoading}
            />
          </div>
          <Button
            variant="secondary"
            onClick={handleRefresh}
            isLoading={isLoading || isOptionsLoading}
          >
            Refresh
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleDownload("csv")}
            isLoading={downloadingFormat === "csv"}
            disabled={Boolean(downloadingFormat)}
          >
            CSV
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleDownload("pdf")}
            isLoading={downloadingFormat === "pdf"}
            disabled={Boolean(downloadingFormat)}
          >
            PDF
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm font-medium text-danger">
          {error}
        </div>
      )}

      {/* <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Total Addition
          </p>
          <p className="mt-1 text-2xl font-black text-emerald-600">
            {formatWeight(
              rows.reduce((acc, curr) => acc + Number(curr.totalAddition || 0), 0)
            )}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Total Deduction
          </p>
          <p className="mt-1 text-2xl font-black text-rose-600">
            {formatWeight(
              rows.reduce((acc, curr) => acc + Number(curr.totalDeduction || 0), 0)
            )}
          </p>
        </div>
        <div className="rounded-xl border border-primary-200 bg-primary-50 p-4 shadow-sm dark:border-primary-900/50 dark:bg-primary-900/10">
          <p className="text-[10px] font-bold uppercase tracking-wider text-primary-700 dark:text-primary-300">
            Remaining Stock
          </p>
          <p className="mt-1 text-2xl font-black text-primary-800 dark:text-primary-200">
            {formatWeight(
              rows.reduce((acc, curr) => acc + Number(curr.remainingStock || 0), 0)
            )}
          </p>
        </div>
      </div> */}

      <div className="flex min-h-0 flex-col overflow-visible rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="relative overflow-auto" >
          <table className="w-full min-w-max border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-slate-50/80 backdrop-blur-md dark:bg-slate-800/80">
              <tr>
                <th className="border-b border-slate-200 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400 w-16">
                  Sr. No.
                </th>
                <th className="border-b border-slate-200 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400 w-16">
                  Details
                </th>
                <th className="border-b border-slate-200 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  Stock Category
                </th>
                <th className="border-b border-slate-200 px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  Addition
                </th>
                <th className="border-b border-slate-200 px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  Deduction
                </th>
                
                <th className="border-b border-slate-200 px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  Remaining Stock
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
                      <p className="text-xs font-medium text-slate-500">
                        Loading records...
                      </p>
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <svg className="h-10 w-10 text-slate-300 dark:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                        No records found
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((item, index) => {
                  const isExpanded = expandedRows[index];
                  const stockData = item.stockData || [];

                  return (
                    <React.Fragment key={index}>
                      <tr
                        className={`group transition-colors cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-800/50 ${isExpanded ? "bg-slate-50/80 dark:bg-slate-800/80" : ""}`}
                        onClick={() => toggleRow(index)}
                      >
                        <td className="px-4 py-3 text-xs font-medium text-slate-500">
                          {index + 1}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            className={`flex h-6 w-6 items-center justify-center rounded-full transition-all ${
                              isExpanded
                                ? "bg-primary-100 text-primary-600 dark:bg-primary-900/50 dark:text-primary-400"
                                : "bg-slate-100 text-slate-500 group-hover:bg-primary-50 group-hover:text-primary-500 dark:bg-slate-800 dark:text-slate-400"
                            }`}
                          >
                            <svg
                              className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </td>
                        <td className="px-4 py-3 text-xs font-bold text-slate-900 dark:text-white">
                          {item.stockCategoryName}
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-mono font-bold text-emerald-600">
                          {formatWeight(item.totalAddition)}
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-mono font-bold text-rose-600">
                          {formatWeight(item.totalDeduction)}
                        </td>
                        
                        <td className="px-4 py-3 text-right text-xs font-mono font-black text-primary-700 dark:text-primary-400">
                          {formatWeight(item.remainingStock)}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td
                            colSpan="7"
                            className="bg-slate-50/50 px-6 py-4 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800"
                          >
                            <div className="max-w-5xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                              <div className="bg-slate-100 px-4 py-2 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Transaction History</span>
                              </div>
                              <table className="w-full text-left">
                                <thead className="bg-slate-50/50 dark:bg-slate-800/50">
                                  <tr>
                                    <th className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Date</th>
                                    <th className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Lot</th>
                                    <th className="px-4 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Addition</th>
                                    <th className="px-4 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Deduction</th>
                                    <th className="px-4 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Cumulative</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                  {stockData.length === 0 ? (
                                    <tr>
                                      <td colSpan="8" className="px-4 py-8 text-center text-xs font-medium text-slate-500">
                                        No transaction records found.
                                      </td>
                                    </tr>
                                  ) : (
                                    stockData.map((detail, dIndex) => (
                                      <tr key={dIndex} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                                        <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
                                          {formatDate(detail.transactionDate)}
                                        </td>
                                        
                                        <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
                                          {detail.lotNo || detail.sourceLotNo || "-"}
                                        </td>
                                        <td className="px-4 py-2 text-right text-xs font-mono font-medium text-emerald-600">
                                          {getDetailAddition(detail) > 0 ? `+${formatWeight(getDetailAddition(detail))}` : "-"}
                                        </td>
                                        <td className="px-4 py-2 text-right text-xs font-mono font-medium text-rose-600">
                                          {getDetailDeduction(detail) > 0 ? `-${formatWeight(getDetailDeduction(detail))}` : "-"}
                                        </td>
                                        
                                        <td className="px-4 py-2 text-right text-xs font-mono font-bold text-slate-900 dark:text-white">
                                          {formatWeight(detail.cumulativeStock)}
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default StockReportPage;
